import uuid from 'react-uuid';
import jwt from 'jsonwebtoken';
import { getToken } from '../auth/AuthActions';

export const BID_PLACED = 'BID_PLACED';
export const BID_REMOVED = 'BID_REMOVED';
export const AUCTION_WAITING = 'AUCTION_WAITING';
export const AUCTION_TO_ACCEPT = 'AUCTION_TO_ACCEPT';
export const AUCTION_SOLD = 'AUCTION_SOLD';
export const AUCTION_REJECTED = 'AUCTION_REJECTED';
export const AUCTION_CANCELLED = 'AUCTION_CANCELLED';
export const AUCTION_UPDATED = 'AUCTION_UPDATED';
export const AUCTION_DELETED = 'AUCTION_DELETED';
export const AUCTION_ENDED = 'AUCTION_ENDED';
export const AUCTION_PUBLISHED = 'AUCTION_PUBLISHED';
export const AUCTION_ENDS_ON_REFRESHED = 'AUCTION_ENDS_ON_REFRESHED';

export const SSE_BUYER = 'SSE_BUYER';
export const SSE_SELLER = 'SSE_SELLER';

const SSE_URL = process.env.REACT_APP_SSE_URL || '';
const DEVICE_ID = 'DEVICE_ID';

const getDeviceId = () => {
  let deviceId = sessionStorage.getItem(DEVICE_ID);
  if (deviceId) {
    return deviceId;
  }

  deviceId = uuid();
  sessionStorage.setItem(DEVICE_ID, deviceId);
  return deviceId;
};

export class SSEClient {
  constructor(type) {
    this.client = null;
    this.type = type;
    this._idsSig = '';
    this._callback = null;
  }

  // normalize, dedupe, and sort IDs to a stable signature
  static _makeSig(auctionsIds) {
    if (!Array.isArray(auctionsIds) || auctionsIds.length === 0) return '';
    const sig = auctionsIds
      .filter((id) => id !== undefined && id !== null)
      .map((id) => String(id))
      .sort()
      .join(',');
    return sig;
  }

  listen(auctionsIds, callback) {
    const sig = SSEClient._makeSig(auctionsIds);

    // if nothing to listen to, close any existing connection
    if (!sig) {
      this.close();
      this._idsSig = '';
      this._callback = null;
      return;
    }

    // if we already have an open connection for the same set of IDs,
    // just update the callback and avoid reconnecting
    if (this.client && this._idsSig === sig) {
      this._callback = callback;
      return;
    }

    const token = getToken();
    const decodedToken = jwt.decode(token);

    // recreate the connection for a new set of IDs
    this.close();
    this._idsSig = sig;
    this._callback = callback;

    const deviceId = getDeviceId();
    const params = new URLSearchParams();
    sig.split(',').forEach((id) => params.append('auctionIds', id));
    if (decodedToken?._st) params.append('_st', decodedToken._st);
    params.append('deviceId', deviceId);
    const query = params.toString();

    this.client = new EventSource(
      `${SSE_URL}/sse/auction/${this.type === SSE_BUYER ? 'buyer' : 'seller'}?${query}`,
    );

    const onEvent = (e) => {
      try {
        const parsed = JSON.parse(e.data);
        const data = parsed && typeof parsed === 'object' ? { ...parsed } : parsed;
        if (data && typeof data === 'object' && !data.type && e?.type) {
          data.type = e.type;
        }
        if (typeof this._callback === 'function') this._callback(data);
      } catch (err) {
        // fallback: deliver raw data if JSON parse fails
        if (typeof this._callback === 'function') this._callback(e.data);
      }
    };

    this.client.addEventListener(BID_PLACED, onEvent, false);
    this.client.addEventListener(BID_REMOVED, onEvent, false);
    this.client.addEventListener(AUCTION_WAITING, onEvent, false);
    this.client.addEventListener(AUCTION_TO_ACCEPT, onEvent, false);
    this.client.addEventListener(AUCTION_SOLD, onEvent, false);
    this.client.addEventListener(AUCTION_REJECTED, onEvent, false);
    this.client.addEventListener(AUCTION_CANCELLED, onEvent, false);
    this.client.addEventListener(AUCTION_UPDATED, onEvent, false);
    this.client.addEventListener(AUCTION_DELETED, onEvent, false);
    this.client.addEventListener(AUCTION_ENDED, onEvent, false);
    this.client.addEventListener(AUCTION_PUBLISHED, onEvent, false);
    this.client.addEventListener(AUCTION_ENDS_ON_REFRESHED, onEvent, false);
  }

  close() {
    if (this?.client) {
      this.client.close();
      this.client = null;
    }
  }
}
